import {useImproveAxios} from "@/app2/shared/hooks";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {
	getImproveAPI,
	type GetNotificationsResponse,
	type NotificationLogItemDto,
	type UserControllerGetNotificationsParams
} from "../gen";
import {ANNOUNCEMENTS_QUERY_KEYS} from "./announcements";

export type {GetNotificationsResponse, NotificationLogItemDto};

export type GetNotificationsParams = UserControllerGetNotificationsParams;

export const NOTIFICATIONS_QUERY_KEYS = {
	LIST: "notifications",
	list: (params?: GetNotificationsParams) => [NOTIFICATIONS_QUERY_KEYS.LIST, params] as const
} as const;

/**
 * Get current user's notifications (paginated).
 * Uses generated client: GET /api/_internal/user/notifications.
 */
export const useGetNotifications = (
	params?: GetNotificationsParams,
	options?: {enabled?: boolean}
) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetNotifications} = getImproveAPI();

	return useQuery({
		queryKey: NOTIFICATIONS_QUERY_KEYS.list(params),
		queryFn: async () => {
			const response = await userControllerGetNotifications(params, axiosConfig);
			return response.data;
		},
		staleTime: 60 * 1000,
		enabled: options?.enabled !== false
	});
};

/**
 * Register FCM device token for push notifications.
 * Uses generated client: PUT /api/_internal/user/notification-token.
 */
export const useRegisterNotificationToken = () => {
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();
	const {userControllerRegisterNotificationToken} = getImproveAPI();

	return useMutation({
		mutationFn: async (token: string) => {
			await userControllerRegisterNotificationToken({token}, axiosConfig);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [NOTIFICATIONS_QUERY_KEYS.LIST]});
		}
	});
};

/**
 * Mark all notifications as read for current user.
 * Uses generated client: POST /api/_internal/user/notifications/read.
 * Pass onSuccess to run after success (e.g. refresh list); callbacks in useMutation
 * run even if the component unmounts before the request completes, unlike callbacks passed to mutate().
 */
export const useMarkNotificationsRead = (options?: {onSuccess?: () => void}) => {
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();
	const {userControllerMarkNotificationsRead} = getImproveAPI();

	return useMutation({
		mutationFn: async () => {
			await userControllerMarkNotificationsRead(axiosConfig);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [NOTIFICATIONS_QUERY_KEYS.LIST]});
			queryClient.invalidateQueries({
				queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_NOTIFICATIONS]
			});
			options?.onSuccess?.();
		}
	});
};

/**
 * Mark a single notification as read by ID.
 * Uses generated client: PUT /api/_internal/user/notifications/{id}/read.
 */
export const useMarkNotificationReadById = (options?: {onSuccess?: () => void}) => {
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();
	const {userControllerMarkNotificationRead} = getImproveAPI();

	return useMutation({
		mutationFn: async (notificationId: number) => {
			await userControllerMarkNotificationRead(notificationId, axiosConfig);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [NOTIFICATIONS_QUERY_KEYS.LIST]});
			queryClient.invalidateQueries({
				queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_NOTIFICATIONS]
			});
			options?.onSuccess?.();
		}
	});
};
